import { Vector3 } from 'three';
import { BALANCE } from '../config/balance';
import type { CollisionWorld } from '../world/CollisionWorld';
import { raySphere } from './Hitscan';

export interface Projectile {
  position: Vector3;
  velocity: Vector3;
  /** Where the bolt was fired from, used for the muzzle flash and damage cue. */
  origin: Vector3;
  life: number;
  damage: number;
}

const scratchSegment = new Vector3();
const scratchImpact = new Vector3();

/**
 * Slow energy bolts fired by the drones. They are checked against the world
 * and the player with a segment test so fast bolts cannot tunnel.
 */
export class ProjectileSystem {
  private readonly projectiles: Projectile[] = [];

  get active(): readonly Projectile[] {
    return this.projectiles;
  }

  spawn(position: Vector3, direction: Vector3, speed: number, damage: number, life: number): void {
    this.projectiles.push({
      position: position.clone(),
      velocity: direction.clone().normalize().multiplyScalar(speed),
      origin: position.clone(),
      life,
      damage,
    });
  }

  clear(): void {
    this.projectiles.length = 0;
  }

  update(
    dt: number,
    world: CollisionWorld,
    playerPosition: Vector3,
    playerRadius: number,
    onPlayerHit: (damage: number, position: Vector3, source: Vector3) => void,
    onWorldHit?: (position: Vector3) => void,
  ): void {
    for (let index = this.projectiles.length - 1; index >= 0; index -= 1) {
      const projectile = this.projectiles[index] as Projectile;
      projectile.life -= dt;
      if (projectile.life <= 0) {
        this.projectiles.splice(index, 1);
        continue;
      }

      scratchSegment.copy(projectile.velocity).multiplyScalar(dt);
      const stepLength = scratchSegment.length();
      if (stepLength > 1e-5) {
        scratchSegment.divideScalar(stepLength);

        const playerHit = raySphere(
          projectile.position,
          scratchSegment,
          playerPosition,
          playerRadius,
          stepLength,
        );
        if (playerHit !== null) {
          scratchImpact.copy(projectile.position).addScaledVector(scratchSegment, playerHit);
          onPlayerHit(projectile.damage, scratchImpact, projectile.origin);
          this.projectiles.splice(index, 1);
          continue;
        }

        const worldHit = world.raycast(projectile.position, scratchSegment, stepLength);
        if (worldHit) {
          onWorldHit?.(worldHit.point);
          this.projectiles.splice(index, 1);
          continue;
        }
      }

      projectile.position.addScaledVector(projectile.velocity, dt);
    }
  }

  static defaultBoltDamage(): number {
    return BALANCE.combat.boltDamage;
  }
}
